import { useEffect } from 'react';

import { isSameMonth, startOfMonth } from 'date-fns';
import { Frown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { useApiError } from '@/modules/common/hooks/use-api-error';

import { useMonthOverview } from '../../hooks/use-month-overview';
import { useViewModeAndDate } from '../../hooks/use-view-mode-and-date';
import { DayDetailPanel } from './day-detail-panel';
import { MonthCalendar } from './month-calendar';
import { MonthViewSkeleton } from './month-view-skeleton';
import { ProjectsThisMonthCard } from './projects-this-month-card';
import { TimeOffsThisMonthCard } from './time-offs-this-month-card';

export function MonthView() {
  const { t } = useTranslation('authenticated.timeTracking');
  const { getErrorMessage } = useApiError();
  const { selectedDate, selectedDay, setSelectedDay } = useViewModeAndDate();

  const { data, isLoading, isRefetching, error, refetch } = useMonthOverview({
    date: selectedDate,
  });

  // Default the selected-day anchor when navigating into the month view or
  // moving to a different month. Prefer "today" when today falls inside the
  // visible month, otherwise the first of the month.
  useEffect(() => {
    if (selectedDay && isSameMonth(selectedDay, selectedDate)) return;
    const today = new Date();
    const defaultDay = isSameMonth(today, selectedDate) ? today : startOfMonth(selectedDate);
    setSelectedDay(defaultDay);
    // We only react to the visible month changing — `selectedDay` is what we
    // would set, so leave it out to avoid an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate.getFullYear(), selectedDate.getMonth()]);

  if (isLoading) return <MonthViewSkeleton />;

  if (error) {
    return (
      <Empty className="rounded-lg border bg-card">
        <EmptyMedia variant="icon">
          <Frown />
        </EmptyMedia>
        <EmptyTitle>{t('monthView.error.title')}</EmptyTitle>
        <EmptyDescription>{getErrorMessage(error)}</EmptyDescription>
        <EmptyContent>
          <ButtonLoadable
            variant="outline"
            size="sm"
            showRefreshIcon
            isLoading={isRefetching}
            onClick={() => refetch()}
          >
            {t('monthView.error.retry')}
          </ButtonLoadable>
        </EmptyContent>
      </Empty>
    );
  }

  if (!data) return null;

  // Guard for the brief tick where `selectedDay` may be outside the visible
  // month before the effect above fires.
  const effectiveSelectedDay =
    selectedDay && isSameMonth(selectedDay, selectedDate) ? selectedDay : null;

  return (
    <div className="flex flex-col gap-4 xl:grid xl:grid-cols-[1fr_360px] xl:items-start">
      {/* On xl the calendar pins to the top of the page scroll container while
          the taller right-hand panel (day detail + projects + time offs)
          scrolls past it, so the grid stays visible at any scroll position. */}
      <div className="min-w-0 xl:sticky xl:top-0">
        <MonthCalendar
          monthAnchor={selectedDate}
          overview={data}
          selectedDay={effectiveSelectedDay}
          onSelectDay={setSelectedDay}
        />
      </div>
      <div className="flex flex-col gap-4">
        <DayDetailPanel selectedDay={effectiveSelectedDay} />
        <ProjectsThisMonthCard projects={data.monthlyProjects} />
        <TimeOffsThisMonthCard requests={data.timeOffRequests} />
      </div>
    </div>
  );
}
